<template>
<div>
  <navigation></navigation>
  <div class="my-orders-page">
    <div class="container">
      <div class="page-header">
        <h1>My Orders</h1>
        <p>View and track your order history</p>
      </div>

      <div v-if="loading" class="loading">
        <div class="spinner"></div>
        Loading your orders...
      </div>

      <div v-else-if="error" class="error-state">
        <div class="error-icon">⚠️</div>
        <p>{{ error }}</p>
      </div>

      <div v-else-if="orders.length === 0" class="empty-state">
        <div class="empty-icon">📦</div>
        <h2>No orders yet</h2>
        <p>You haven't placed any orders yet. Start shopping!</p>
        <button class="btn-shop" @click="$router.push('/shop')">
          Browse Products
        </button>
      </div>

      <div v-else class="orders-list">
        <div
          v-for="order in orders"
          :key="order.id"
          class="order-card"
          @click="viewOrder(order.id)"
        >
          <div class="order-header">
            <div class="order-info">
              <h3>Order #{{ order.order_number }}</h3>
              <p class="order-date">{{ formatDate(order.created_at) }}</p>
            </div>
            <div class="order-status">
              <span :class="['status-badge', order.status]">
                {{ formatStatus(order.status) }}
              </span>
            </div>
          </div>

          <div class="order-items-preview">
            <div
              v-for="item in order.items.slice(0, 3)"
              :key="item.id"
              class="item-preview"
            >
              <div class="item-image-small">
                <img
                  v-if="item.product && item.product.images && item.product.images.length > 0"
                  :src="getPrimaryImage(item.product).image_url"
                  :alt="item.product_name"
                />
                <div v-else class="no-image-small">?</div>
              </div>
            </div>
            <span v-if="order.items.length > 3" class="more-items">
              +{{ order.items.length - 3 }} more
            </span>
          </div>

          <div class="order-footer">
            <div class="order-total">
              <span class="total-label">Total:</span>
              <span class="total-amount">${{ formatPrice(order.total_cents) }}</span>
            </div>
            <button class="btn-view" @click.stop="viewOrder(order.id)">
              View Details →
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import { mapState } from 'vuex'
import ProductRepository from '../data/product.repository'
import Nav from '../components/Nav'

export default {
  name: 'MyOrders',
  components: {
    'navigation': Nav
  },
  data () {
    return {
      orders: [],
      loading: false,
      error: null
    }
  },
  computed: {
    ...mapState(['isAuthenticated', 'currentUser'])
  },
  mounted () {
    if (!this.isAuthenticated) {
      this.$router.push('/login')
      return
    }
    this.loadOrders()
  },
  methods: {
    loadOrders () {
      this.loading = true
      this.error = null

      ProductRepository.getUserOrders()
        .then((orders) => {
          this.orders = orders
          this.loading = false
        })
        .catch((err) => {
          this.loading = false
          this.error = 'Failed to load orders. Please try again.'
          console.error('Error loading orders:', err)
        })
    },

    viewOrder (orderId) {
      this.$router.push(`/order-confirmation/${orderId}`)
    },

    getPrimaryImage (product) {
      if (!product.images || product.images.length === 0) return null
      return product.images.find(img => img.is_primary === 1) || product.images[0]
    },

    formatPrice (cents) {
      return (cents / 100).toFixed(2)
    },

    formatStatus (status) {
      return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')
    },

    formatDate (dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.my-orders-page {
  padding: 20px 0;
  min-height: 60vh;
  background: #f7fafc;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.page-header {
  text-align: center;
  padding: 40px 20px 30px;
}

.page-header h1 {
  font-size: 32px;
  font-weight: bold;
  margin: 0 0 8px 0;
  color: #2d3748;
}

.page-header p {
  font-size: 16px;
  color: #718096;
  margin: 0;
}

.loading {
  text-align: center;
  padding: 60px 20px;
  font-size: 18px;
  color: #4a5568;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top-color: #3182ce;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-state {
  text-align: center;
  padding: 60px 20px;
}

.error-icon {
  font-size: 60px;
  margin-bottom: 16px;
}

.error-state p {
  font-size: 18px;
  color: #e53e3e;
}

.empty-state {
  text-align: center;
  padding: 80px 20px;
}

.empty-icon {
  font-size: 80px;
  margin-bottom: 20px;
}

.empty-state h2 {
  font-size: 24px;
  font-weight: bold;
  margin: 0 0 12px 0;
  color: #2d3748;
}

.empty-state p {
  font-size: 16px;
  color: #718096;
  margin: 0 0 24px 0;
}

.btn-shop {
  background: #3182ce;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-shop:hover {
  background: #2c5aa0;
}

/* Orders List */
.orders-list {
  display: grid;
  gap: 16px;
}

.order-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s;
}

.order-card:hover {
  border-color: #3182ce;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  transform: translateY(-2px);
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.order-info h3 {
  font-size: 18px;
  font-weight: bold;
  margin: 0 0 4px 0;
  color: #2d3748;
  word-break: break-word;
}

.order-date {
  font-size: 14px;
  color: #718096;
  margin: 0;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.pending {
  background: #fef5e7;
  color: #f39c12;
}

.status-badge.paid {
  background: #d4edda;
  color: #28a745;
}

.status-badge.processing {
  background: #d1ecf1;
  color: #0c5460;
}

.status-badge.shipped {
  background: #cce5ff;
  color: #004085;
}

.status-badge.delivered {
  background: #d4edda;
  color: #155724;
}

/* Order Items Preview */
.order-items-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 12px 0;
  border-top: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
}

.item-preview {
  flex-shrink: 0;
}

.item-image-small {
  width: 50px;
  height: 50px;
  background: #f7fafc;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.item-image-small img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image-small {
  color: #cbd5e0;
  font-size: 20px;
  font-weight: bold;
}

.more-items {
  font-size: 14px;
  color: #718096;
  margin-left: 4px;
}

/* Order Footer */
.order-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.order-total {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.total-label {
  font-size: 14px;
  color: #718096;
}

.total-amount {
  font-size: 20px;
  font-weight: bold;
  color: #2d3748;
}

.btn-view {
  background: #3182ce;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-view:hover {
  background: #2c5aa0;
}

/* Responsive */
@media (max-width: 768px) {
  .page-header h1 {
    font-size: 24px;
  }

  .order-header {
    flex-direction: column;
    gap: 8px;
  }

  .order-footer {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .btn-view {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// MyOrders.vue?070c6a93